import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, HttpError } from "../api/rest";
import { accountHint, shortFunder } from "../lib/accounts";
import { bindingOf, bindingView, type AnySubsystem } from "../lib/subsystems";
import { useAccounts } from "../store/useAccounts";

/**
 * Аккаунт, от имени которого подсистема торгует сама (T5.4).
 *
 * Рядом с переключателем режима подсистемы, а не на отдельной странице: режим
 * «торговать» без назначенного аккаунта не торгует, и узнать об этом оператор
 * должен там же, где режим включает.
 *
 * Выбор не подсвечивается оптимистично: нажатие пишет ключ и ждёт перечтения —
 * иначе панель показывала бы аккаунт, которым трейдер ещё не торгует.
 */
export function SubsystemAccountSelect({ subsystem }: { subsystem: AnySubsystem }) {
  const queryClient = useQueryClient();
  const accounts = useAccounts((s) => s.data);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bindings = useQuery({
    queryKey: ["subsystem-accounts"],
    queryFn: () => api.subsystemAccounts(),
    refetchInterval: 10_000,
  });

  const binding = bindingOf(bindings.data, subsystem);
  const view = bindingView(binding);
  const value = binding?.account ?? "";
  const rows = accounts?.accounts ?? [];
  // Привязка к выбывшему из состава аккаунту — отдельной строкой: молча
  // показать «не назначен» значило бы скрыть, что ключ в Redis всё ещё стоит.
  const orphan = value && !rows.some((a) => a.funder === value) ? value : null;

  async function pick(funder: string) {
    setBusy(true);
    setError(null);
    try {
      const next = await api.setSubsystemAccounts({ [subsystem]: funder || null });
      queryClient.setQueryData(["subsystem-accounts"], next);
    } catch (err) {
      setError(err instanceof HttpError ? err.message : "не удалось записать привязку");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5 text-xs">
      <span className="shrink-0 text-[var(--color-muted)]">аккаунт</span>
      <select
        value={value}
        disabled={busy || !bindings.data}
        onChange={(e) => void pick(e.target.value)}
        className="shrink-0 rounded border border-[var(--color-border)] bg-transparent px-1.5 py-0.5 disabled:opacity-50"
        title="от чьего имени подсистема ставит ордера сама; не назначен — не торгует"
      >
        <option value="">не назначен</option>
        {rows.map((a) => (
          <option key={a.funder} value={a.funder}>
            {a.slot}
            {accountHint(a) ? ` — ${accountHint(a)}` : ""}
          </option>
        ))}
        {orphan && <option value={orphan}>{shortFunder(orphan)} — вне состава</option>}
      </select>
      <span
        className={`min-w-0 truncate ${
          view.trading === false ? "text-amber-300" : "text-[var(--color-muted)]"
        }`}
        title={view.label}
      >
        {bindings.error && !bindings.data ? "привязки не прочитаны" : view.label}
      </span>
      {error && <span className="text-[var(--color-sell)]">{error}</span>}
    </div>
  );
}
